import { useState, useEffect } from 'react';
import { service } from '../components/desktopUpload/service';

const DEFAULT_CONFIG = {
  chunk_size: 10 * 1024 * 1024,
  parallel_chunks: 4,
  presign_batch_size: 10,
  max_retries: 3,
  retry_delays: [2000, 4000, 8000],
  poll_interval: 3000
};

const MAX_CONCURRENT_FILE_UPLOADS = 3;

const VIDEO_SIGNATURES = [
  { offset: 4, hex: '66747970', label: 'mp4/mov' },
  { offset: 0, hex: '1a45dfa3', label: 'mkv/webm' },
  { offset: 0, hex: '52494646', label: 'avi/wav' },
  { offset: 0, hex: '464c5601', label: 'flv' },
  { offset: 0, hex: '3026b275', label: 'wmv/asf' },
  { offset: 0, hex: '47', label: 'ts' },
  { offset: 0, hex: '494433', label: 'mp3' },
  { offset: 0, hex: 'fffb', label: 'mp3' },
  { offset: 0, hex: 'fff3', label: 'mp3' },
  { offset: 0, hex: '4f676753', label: 'ogg' },
  { offset: 0, hex: '664c6143', label: 'flac' },
  { offset: 0, hex: 'fff1', label: 'aac' },
  { offset: 0, hex: 'fff9', label: 'aac' },
];

function matchesHex(bytes, offset, hexStr) {
  const needed = Math.ceil(hexStr.length / 2);
  if (bytes.length < offset + needed) return false;
  for (let i = 0; i < hexStr.length; i += 2) {
    const byteIndex = offset + i / 2;
    const expected = parseInt(hexStr.substr(i, 2), 16);
    if (bytes[byteIndex] !== expected) return false;
  }
  return true;
}

export function getExtension(filename) {
  if (!filename || typeof filename !== 'string') return '';
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0) return '';
  return filename.substring(lastDot);
}

export function getBaseName(filename) {
  if (!filename || typeof filename !== 'string') return filename || '';
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0) return filename;
  return filename.substring(0, lastDot);
}

export class UploadManager {
  constructor() {
    this.uploads = {};
    this.listeners = [];
    this.paused = false;
    this.channelId = null;
    this.tabId = Math.random().toString(36).substring(2, 15);

    if (typeof window !== 'undefined') {
      window.addEventListener('offline', () => this.setPaused(true));
      window.addEventListener('online', () => this.setPaused(false));

      window.addEventListener('beforeunload', (e) => {
        if (this.getActiveCount() > 0) {
          e.preventDefault();
          e.returnValue = ''; // Setting returnValue triggers the browser's default prompt
          return '';
        }
      });

      window.addEventListener('pagehide', () => {
        localStorage.removeItem(`active_uploads_tab_${this.tabId}`);
        const activeUploads = Object.values(this.uploads).filter(
          u => ['queued', 'uploading', 'paused', 'processing'].includes(u.status)
        );
        if (activeUploads.length > 0) {
          activeUploads.forEach(u => {
            const url = `${process.env.REACT_APP_API_SERVER_HOST}/desktopUpload/abort`;
            const headers = {
              'Content-Type': 'application/json',
              'access-token': localStorage.getItem('access-token') || '',
              'pubid': localStorage.getItem('pub_id') || '',
              'channelid': u.channelId || '',
              'role_id': localStorage.getItem('role_id') || ''
            };
            fetch(url, {
              method: 'POST',
              headers,
              body: JSON.stringify({ upload_id: u.uploadId }),
              keepalive: true
            }).catch(() => {});
          });
        }
      });

      this.startHeartbeat();
    }
  }

  startHeartbeat() {
    const updateHeartbeat = () => {
      const activeIds = Object.keys(this.uploads).filter(id => 
        ['queued', 'uploading', 'paused', 'processing'].includes(this.uploads[id]?.status)
      );
      if (activeIds.length > 0) {
        localStorage.setItem(`active_uploads_tab_${this.tabId}`, JSON.stringify({
          ids: activeIds,
          timestamp: Date.now()
        }));
      } else {
        localStorage.removeItem(`active_uploads_tab_${this.tabId}`);
      }
    };
    
    updateHeartbeat();
    setInterval(updateHeartbeat, 5000);
  }

  getActiveUploadIdsAcrossTabs() {
    const activeIds = new Set();
    
    Object.keys(this.uploads).forEach(id => {
      const u = this.uploads[id];
      if (u && ['queued', 'uploading', 'paused', 'processing'].includes(u.status)) {
        activeIds.add(String(id));
      }
    });

    const now = Date.now();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('active_uploads_tab_')) {
        if (key === `active_uploads_tab_${this.tabId}`) continue;
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (now - data.timestamp < 15000) {
            data.ids.forEach(id => activeIds.add(String(id)));
          } else {
            localStorage.removeItem(key);
          }
        } catch (e) {}
      }
    }
    return activeIds;
  }


  async validateMicrodramaAspectRatio(file) {
    if (!file.type || !file.type.startsWith('video/')) return true;
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      const objectUrl = URL.createObjectURL(file);
      video.src = objectUrl;
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(objectUrl);
        if (!(video.videoHeight > video.videoWidth)) {
          return reject(new Error('Only microdrama aspect ratio (portrait orientation: height > width) videos are allowed.'));
        }
        resolve(true);
      };
      video.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(true);
      };
    });
  }

  async validateFile(file, options = {}) {
    const isVertical = options.isVertical || options.is_vertical;
    if (isVertical) {
      const MAX_VERTICAL_FILE_SIZE = 700 * 1024 * 1024; // 700 MB
      if (file.size > MAX_VERTICAL_FILE_SIZE) {
        throw new Error('File size exceeds the limit of 700 MB for a vertical microdrama video.');
      }
      await this.validateMicrodramaAspectRatio(file);
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arr = new Uint8Array(e.target.result);

        for (const sig of VIDEO_SIGNATURES) {
          if (matchesHex(arr, sig.offset, sig.hex)) {
            return resolve(true);
          }
        }

        if (file.size === 0) {
          return reject(new Error('The selected file is empty.'));
        }

        return reject(new Error(
          'Invalid file format. The file header does not match any known video or audio format. ' +
          'Please ensure you are uploading a valid video/audio file (MP4, MKV, AVI, MOV, TS, FLV, MP3, etc.).'
        ));
      };
      reader.onerror = () => reject(new Error('Failed to read file for format validation.'));
      reader.readAsArrayBuffer(file.slice(0, 32));
    });
  }

  isDuplicate(filename, fileSize) {
    return Object.values(this.uploads).some(
      u =>
        u.filename === filename &&
        u.fileSize === fileSize &&
        ['queued', 'uploading', 'processing'].includes(u.status)
    );
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notify() {
    if (typeof window !== 'undefined') {
      const activeIds = Object.keys(this.uploads).filter(id => 
        ['queued', 'uploading', 'paused', 'processing'].includes(this.uploads[id]?.status)
      );
      if (activeIds.length > 0) {
        localStorage.setItem(`active_uploads_tab_${this.tabId}`, JSON.stringify({
          ids: activeIds,
          timestamp: Date.now()
        }));
      } else {
        localStorage.removeItem(`active_uploads_tab_${this.tabId}`);
      }
    }
    this.listeners.forEach(listener => listener());
  }

  setChannelId(channelId) {
    this.channelId = channelId;
    this.notify();
  }

  setPaused(paused) {
    this.paused = paused;
    Object.keys(this.uploads).forEach(id => {
      if (this.uploads[id].status === 'uploading' && paused) {
        this.updateUpload(id, { status: 'paused' });
      } else if (this.uploads[id].status === 'paused' && !paused) {
        this.updateUpload(id, { status: 'uploading' });
      }
    });
  }

  updateUpload(uploadId, patch) {
    if (this.uploads[uploadId]) {
      this.uploads[uploadId] = { ...this.uploads[uploadId], ...patch };
      this.notify();
    }
  }

  async startUpload(file, channelId, customFilename, preview, folder = '/', isVertical = false, extraOptions = {}) {
    const filename = customFilename || file.name || file.filename || file.path || 'video.mp4';
    const fileSize = file.size || 0;
    const mimeType = file.type || 'video/mp4';

    if (isVertical) {
      const MAX_VERTICAL_FILE_SIZE = 700 * 1024 * 1024; // 700 MB
      if (fileSize > MAX_VERTICAL_FILE_SIZE) {
        return { success: false, message: 'File size exceeds the limit of 700 MB for a vertical microdrama video.' };
      }
    } else {
      const MAX_FILE_SIZE = 100 * 1024 * 1024 * 1024; // 100 GB
      if (fileSize > MAX_FILE_SIZE) {
        return { success: false, message: 'File size exceeds the maximum limit of 100 GB.' };
      }
    }

    if (this.isDuplicate(filename, fileSize)) {
      return { success: false, message: `"${filename}" is already being uploaded. Please wait for it to complete.` };
    }

    try {
      await this.validateFile(file, { isVertical });
    } catch (vErr) {
      throw new Error(`Format check failed: ${vErr.message}`);
    }

    let uploadId, uploadConfig;

    try {
      const approxParts = Math.ceil(fileSize / DEFAULT_CONFIG.chunk_size);
      const data = await service.initiateDesktopUpload(filename, mimeType, fileSize, channelId, approxParts, folder, isVertical);
      if (!data.success) {
        return { success: false, message: data.message };
      }
      uploadId = data.upload_id;
      uploadConfig = { ...DEFAULT_CONFIG, ...(data.config || {}) };
    } catch (err) {
      const serverMessage = err.response?.data?.message || err.message;
      return { success: false, message: serverMessage };
    }

    this.uploads[uploadId] = {
      uploadId,
      filename,
      fileSize,
      channelId,
      progress: 0,
      uploadedBytes: 0,
      status: 'queued',
      cleanS3Key: null,
      error: null,
      preview,
      file,
      customFilename,
      uploadConfig,
      completedParts: [],
      folder,
      isVertical: Boolean(isVertical),
      onSuccess: extraOptions?.onSuccess || null
    };
    this.notify();

    this.processQueue();
    return { success: true, uploadId };
  }

  async processQueue() {
    const uploadList = Object.values(this.uploads);

    const normalActive = uploadList.filter(u => !u.isVertical && ['uploading', 'scanning'].includes(u.status)).length;
    if (normalActive < MAX_CONCURRENT_FILE_UPLOADS) {
      const nextNormal = uploadList.find(u => !u.isVertical && u.status === 'queued');
      if (nextNormal) {
        this.updateUpload(nextNormal.uploadId, { status: 'uploading' });
        this.runUploadTask(nextNormal.uploadId);
        this.processQueue();
      }
    }

    const verticalActive = uploadList.filter(u => u.isVertical && ['uploading', 'scanning'].includes(u.status)).length;
    if (verticalActive < MAX_CONCURRENT_FILE_UPLOADS) {
      const nextVertical = uploadList.find(u => u.isVertical && u.status === 'queued');
      if (nextVertical) {
        this.updateUpload(nextVertical.uploadId, { status: 'uploading' });
        this.runUploadTask(nextVertical.uploadId);
        this.processQueue();
      }
    }
  }

  async runUploadTask(uploadId) {
    const entry = this.uploads[uploadId];
    if (!entry) return;

    const { file, channelId, customFilename, uploadConfig, completedParts } = entry;
    const filename = customFilename || file.name || 'video.mp4';
    const fileSize = file.size;

    const currentChunkSize = uploadConfig.chunk_size;
    const totalParts = Math.ceil(fileSize / currentChunkSize);
    const remainingParts = Array.from({ length: totalParts }, (_, i) => i + 1);

    let presignedUrlCache = {};
    let activeBatchPromises = {};

    const ensurePresigned = async (partNumbers) => {
      const needed = partNumbers.filter(p => !presignedUrlCache[p]);
      if (needed.length === 0) return;

      const batchId = Math.floor((partNumbers[0] - 1) / uploadConfig.presign_batch_size);

      if (!activeBatchPromises[batchId]) {
        activeBatchPromises[batchId] = (async () => {
          try {
            const data = await service.getDesktopUploadPresignedUrls(uploadId, needed, channelId);
            if (!data.success) throw new Error(data.message);
            Object.assign(presignedUrlCache, data.presigned_urls);
          } finally {
            delete activeBatchPromises[batchId];
          }
        })();
      }
      await activeBatchPromises[batchId];
    };

    const activeChunkProgress = {};
    let lastProgressUpdate = 0;
    let progressTimeout = null;

    const triggerProgressUpdate = (force = false) => {
      const inFlightBytes = Object.values(activeChunkProgress).reduce((a, b) => a + b, 0);
      const totalCompletedSize = completedParts.length * currentChunkSize;
      const totalUploaded = Math.min(totalCompletedSize + inFlightBytes, fileSize);
      const progress = Math.min(Math.round((totalUploaded / fileSize) * 100), 99);

      const update = () => {
        this.updateUpload(uploadId, { progress, uploadedBytes: totalUploaded });
        lastProgressUpdate = Date.now();
      };

      if (force || Date.now() - lastProgressUpdate >= 250) {
        if (progressTimeout) clearTimeout(progressTimeout);
        update();
      } else if (!progressTimeout) {
        progressTimeout = setTimeout(() => {
          progressTimeout = null;
          update();
        }, 250 - (Date.now() - lastProgressUpdate));
      }
    };

    const tasks = remainingParts.map(partNumber => async () => {
      let etag;
      let lastErr;
      const start = (partNumber - 1) * currentChunkSize;
      const end = Math.min(start + currentChunkSize, fileSize);
      const chunk = file.slice(start, end);

      for (let attempt = 0; attempt < uploadConfig.max_retries; attempt++) {
        try {
          while (this.paused) {
            await new Promise(r => setTimeout(r, 500));
          }

          if (this.uploads[uploadId]?.status === 'failed' || this.uploads[uploadId]?.status === 'aborted') {
            throw new Error('Upload aborted by user');
          }

          const batchStart = Math.floor((partNumber - 1) / uploadConfig.presign_batch_size) * uploadConfig.presign_batch_size + 1;
          const batchNums = Array.from({ length: uploadConfig.presign_batch_size }, (_, i) => batchStart + i)
            .filter(n => n <= totalParts);

          await ensurePresigned(batchNums);

          const presignedUrl = presignedUrlCache[partNumber];
          if (!presignedUrl) throw new Error(`Missing presigned URL for part ${partNumber}`);

          etag = await this.uploadChunkWithRetry(
            presignedUrl, chunk, 1, [0],
            (loaded) => {
              activeChunkProgress[partNumber] = loaded;
              triggerProgressUpdate();
            }
          );
          break;
        } catch (err) {
          lastErr = err;
          delete presignedUrlCache[partNumber];

          while (this.paused) {
            await new Promise(r => setTimeout(r, 500));
          }

          if (this.uploads[uploadId]?.status === 'failed' || this.uploads[uploadId]?.status === 'aborted' || err.message === 'Upload aborted by user') {
            break;
          }

          const delay = uploadConfig.retry_delays[attempt] || 2000;
          await new Promise(r => setTimeout(r, delay));
        }
      }

      if (!etag) {
        throw lastErr;
      }

      delete activeChunkProgress[partNumber];
      const partEntry = { PartNumber: partNumber, ETag: etag };
      completedParts.push(partEntry);
      triggerProgressUpdate(true);

      return partEntry;
    });

    try {
      await this.runInParallel(tasks, uploadConfig.parallel_chunks);
    } catch (err) {
      if (progressTimeout) clearTimeout(progressTimeout);
      const currentStatus = this.uploads[uploadId]?.status;
      const isAborted = currentStatus === 'aborted' || err.message === 'Upload aborted by user' || err.message === 'Upload was cancelled or failed.';
      if (!isAborted) {
        this.updateUpload(uploadId, { status: 'failed', error: err.message });
        try {
          await service.reportDesktopUploadFailure(uploadId, err.message, channelId);
        } catch (reportErr) {
          console.error('Failed to report failure to backend:', reportErr);
        }
      }
      this.processQueue();
      return;
    }

    try {
      await service.completeDesktopUpload(uploadId, completedParts, channelId);
    } catch (err) {
      if (progressTimeout) clearTimeout(progressTimeout);
      const currentStatus = this.uploads[uploadId]?.status;
      const isAborted = currentStatus === 'aborted' || err.message === 'Upload aborted by user' || err.message === 'Upload was cancelled or failed.';
      if (!isAborted) {
        this.updateUpload(uploadId, { status: 'failed', error: err.message });
        try {
          await service.reportDesktopUploadFailure(uploadId, err.message, channelId);
        } catch (reportErr) {
          console.error('Failed to report failure to backend:', reportErr);
        }
      }
      this.processQueue();
      return;
    }

    if (progressTimeout) clearTimeout(progressTimeout);
    this.updateUpload(uploadId, { status: 'processing', progress: 100 });
    this.processQueue();
    this.pollUploadStatus(uploadId, channelId);
  }

  async pollUploadStatus(uploadId, channelId) {
    const entry = this.uploads[uploadId];
    if (!entry) return;

    const interval = entry.uploadConfig?.poll_interval || 3000;

    const check = async () => {
      const currentEntry = this.uploads[uploadId];
      if (!currentEntry || currentEntry.status !== 'processing') return;

      try {
        const data = await service.getDesktopUploadStatus(uploadId, channelId);
        if (data && data.success) {
          const serverStatus = data.status;
          if (serverStatus === 'clean') {
            this.updateUpload(uploadId, { status: 'clean', progress: 100 });
            if (currentEntry.onSuccess && !currentEntry.hasRunOnSuccess) {
              currentEntry.hasRunOnSuccess = true;
              try {
                currentEntry.onSuccess(currentEntry);
              } catch (cbErr) {
                console.error('[UploadManager] onSuccess callback error:', cbErr);
              }
            }
          } else if (serverStatus === 'failed') {
            this.updateUpload(uploadId, { status: 'failed', error: 'Finalization failed on server.' });
          } else {
            setTimeout(check, interval);
          }
        } else {
          setTimeout(check, interval);
        }
      } catch (err) {
        setTimeout(check, interval);
      }
    };

    setTimeout(check, interval);
  }

  async uploadChunkWithRetry(url, chunk, maxRetries, retryDelays, onProgress) {
    let lastErr;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', url, true);
          xhr.timeout = 90000; // 90 seconds timeout
          xhr.setRequestHeader('Content-Type', 'application/octet-stream');
          xhr.upload.onprogress = e => {
            if (e.lengthComputable) onProgress(e.loaded);
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(xhr.getResponseHeader('ETag'));
            } else {
              reject(new Error(`Failed: ${xhr.status}`));
            }
          };
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.ontimeout = () => reject(new Error('Upload request timed out'));
          xhr.send(chunk);
        });
      } catch (err) {
        lastErr = err;
        const delay = retryDelays[attempt] || retryDelays[retryDelays.length - 1];
        await new Promise(r => setTimeout(r, delay));
      }
    }
    throw lastErr;
  }

  async runInParallel(tasks, concurrency) {
    const results = new Array(tasks.length);
    let idx = 0;
    let hasFailed = false;
    const worker = async () => {
      while (idx < tasks.length) {
        if (hasFailed) break;
        const i = idx++;
        try {
          results[i] = await tasks[i]();
        } catch (e) {
          hasFailed = true;
          throw e;
        }
      }
    };
    const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, () => worker());
    await Promise.all(workers);
    return results;
  }

  async cancelUpload(uploadId) {
    this.updateUpload(uploadId, { status: 'aborted' });
    const entry = this.uploads[uploadId];
    if (entry) {
      try {
        await service.abortDesktopUpload(uploadId, entry.channelId);
      } catch (e) {}
    }
    setTimeout(() => {
      delete this.uploads[uploadId];
      this.notify();
      this.processQueue();
    }, 2000);
  }

  async abortAll() {
    const activeUploads = Object.values(this.uploads).filter(
      u => ['queued', 'uploading', 'paused', 'processing'].includes(u.status)
    );
    if (activeUploads.length === 0) return;

    activeUploads.forEach(u => this.updateUpload(u.uploadId, { status: 'aborted' }));

    await Promise.all(
      activeUploads.map(async (u) => {
        try {
          await service.abortDesktopUpload(u.uploadId, u.channelId);
        } catch (e) {}
      })
    );

    activeUploads.forEach(u => {
      delete this.uploads[u.uploadId];
    });
    this.notify();
    this.processQueue();
  }

  dismissUpload(uploadId) {
    delete this.uploads[uploadId];
    this.notify();
    this.processQueue();
  }

  getActiveCount() {
    return Object.values(this.uploads).filter(
      u => ['queued', 'uploading', 'paused', 'processing'].includes(u.status)
    ).length;
  }

  getOverallProgress() {
    const active = Object.values(this.uploads).filter(
      u => ['uploading', 'paused', 'processing', 'queued'].includes(u.status)
    );
    const totalBytes = active.reduce((acc, u) => acc + (u.fileSize || 0), 0);
    const uploadedBytes = active.reduce((acc, u) => acc + (u.uploadedBytes || 0), 0);
    return totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;
  }
}

export const uploadManager = new UploadManager();

export function useUploads() {
  const [uploads, setUploads] = useState(uploadManager.uploads);
  const [channelId, setChannelId] = useState(uploadManager.channelId);

  useEffect(() => {
    return uploadManager.subscribe(() => {
      setUploads({ ...uploadManager.uploads });
      setChannelId(uploadManager.channelId);
    });
  }, []);

  return {
    uploads,
    channelId,
    setChannelId: (cid) => uploadManager.setChannelId(cid),
    startUpload: (file, channelId, customFilename, preview, folder, isVertical, extraOptions) => uploadManager.startUpload(file, channelId, customFilename, preview, folder, isVertical, extraOptions),
    cancelUpload: (uploadId) => uploadManager.cancelUpload(uploadId),
    dismissUpload: (uploadId) => uploadManager.dismissUpload(uploadId),
    abortAll: () => uploadManager.abortAll()
  };
}
